import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  useWindowDimensions,
  View,
} from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

// DEVIATION from the plan: relative imports instead of the '@' alias — see
// CourseVideoPlayer.tsx / useGeneratedCourse.ts for why. This file is
// transitively imported by Task 6's index.test.tsx.
import Box from '../../Atoms/Box';
import CCText from '../../Atoms/CCText';
import {useCourseProgress} from '../../Hooks/LmsCourseApi/useCourseProgress';
import {useGeneratedCourse} from '../../Hooks/LmsCourseApi/useGeneratedCourse';

import CourseButton, {CourseIconButton} from './components/CourseButton';
import {CoachTurn} from '../../Hooks/LmsCourseApi/useAskCoach';
import {transcribeQuestion} from '../../Hooks/LmsCourseApi/transcribeQuestion';

import {browserSpeaker, CoachStep, playScript} from './coachNarration';

import CourseChapterList from './components/CourseChapterList';
import CourseCoachPanel from './components/CourseCoachPanel';
import CoursePuzzleBoard from './components/CoursePuzzleBoard';
import CourseVideoPlayer from './components/CourseVideoPlayer';
import {
  CARD_SHADOW,
  coachLayout,
  coachRailWidth,
  contentWidth,
  F,
  MOBILE_BREAKPOINT,
} from './courseTheme';
import {getVideoSegment, isPuzzlePage, isVideoPage, nextChapterIndex} from './courseLogic';

type Props = {
  bookUuid: string;
  /** Leave the course entirely. Without this the player has no exit at all. */
  onExit?: () => void;
};

export default function CourseChapterScreen({bookUuid, onExit}: Props) {
  const {data: course, isLoading, error} = useGeneratedCourse(bookUuid);
  const progress = useCourseProgress();

  const [index, setIndex] = useState(0);
  const [completed, setCompleted] = useState<Set<number>>(new Set());
  const [showChapters, setShowChapters] = useState(false);
  const [askCoach, setAskCoach] = useState(false);
  // The coach conversation lives here, not in the panel: the student moves
  // between chapters mid-thread (that is what the GO TO buttons are for), and
  // the panel unmounts when they do.
  const [coachTurns, setCoachTurns] = useState<CoachTurn[]>([]);

  // A narrated demonstration in progress. The step lives here because the
  // board is here — the panel is a sibling of it, not a parent.
  const [coachStep, setCoachStep] = useState<CoachStep | null>(null);
  const [playingTurn, setPlayingTurn] = useState<number | null>(null);
  const stopScriptRef = useRef<(() => void) | null>(null);

  const stopScript = useCallback(() => {
    stopScriptRef.current?.();
    stopScriptRef.current = null;
    setCoachStep(null);
    setPlayingTurn(null);
  }, []);

  const playScriptFor = useCallback(
    (steps: CoachStep[], turnIndex: number) => {
      stopScriptRef.current?.();
      setPlayingTurn(turnIndex);
      stopScriptRef.current = playScript(steps, browserSpeaker(), step => {
        setCoachStep(step);
        if (step === null) {
          // Ran to the end on its own.
          stopScriptRef.current = null;
          setPlayingTurn(null);
        }
      });
    },
    [],
  );

  const transcribe = useCallback(
    (audio: Blob) => transcribeQuestion(bookUuid, audio),
    [bookUuid],
  );

  /**
   * A hint, delivered by the coach rather than by a card under the board.
   *
   * Opening the rail is the point: the student pressed a button on the left
   * and the answer appears on the right, so if the rail were shut the press
   * would look like it did nothing. The entry animates in for the same
   * reason — it gives the eye something to follow across.
   */
  const showHint = useCallback(
    (hint: {text: string; square: string | null; level: number}) => {
      const said = hint.square
        ? [hint.text, `The move starts on ${hint.square} — it is outlined on the board.`]
            .filter(Boolean)
            .join(' ')
        : hint.text;
      if (!said) return;
      setAskCoach(true);
      setCoachTurns(turns => {
        // Pressing "See the hint" on a spent hint re-opens the coach on the
        // entry already there. Appending a second identical card would push
        // the first off the top of a 320px rail and read as two separate
        // pieces of advice.
        const last = turns[turns.length - 1];
        if (last?.kind === 'hint' && last.answer === said) return turns;
        return [
          ...turns,
          {
            question: hint.square ? 'Hint · where to look' : 'Hint',
            answer: said,
            chapters: [],
            steps: [],
            kind: 'hint',
          },
        ];
      });
    },
    [],
  );

  // A demonstration must not outlive the chapter it was about, or the screen.
  useEffect(() => stopScript, [stopScript]);
  useEffect(() => {
    stopScript();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  // Where the coach can sit at this width. The board is sized from the window,
  // so it never yields — the lesson card pays for the rail, and below a floor
  // the sheet is the better trade. Mirrors CoursePuzzleBoard's own sizing.
  const {width: windowWidth} = useWindowDimensions();
  const railWidth = coachRailWidth(windowWidth);
  const coachMode = useMemo(() => {
    const available = contentWidth(windowWidth);
    const boardSize =
      available < MOBILE_BREAKPOINT
        ? Math.min(available - 48, 520)
        : Math.min(560, available * 0.42);
    return coachLayout(windowWidth, boardSize);
  }, [windowWidth]);
  // Set while the student has stepped back to re-watch the moment a puzzle
  // came from, so they can be offered a way back to it.
  const [returnToIndex, setReturnToIndex] = useState<number | null>(null);

  const chapters = course?.chapters ?? [];
  const chapter = chapters[index];
  const page = chapter?.pages?.[0];
  // See the comment at the render site: a board inside a scroller loses its
  // drag, so a puzzle chapter gets a plain View.
  const Content: any = page && isPuzzlePage(page) ? View : ScrollView;
  const segment = useMemo(() => (page ? getVideoSegment(page) : null), [page]);

  const markComplete = useCallback(
    (videoWatched: boolean) => {
      if (!course || !chapter || !page) return;
      setCompleted(prev => new Set(prev).add(page.id));
      progress.mutate({
        book_id: course.book.id,
        chapter_id: chapter.id,
        page_id: page.id,
        content_type: page.content_type,
        ...(videoWatched ? {video_watched: true} : {}),
      });
    },
    [course, chapter, page, progress],
  );

  const advanceTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const advance = useCallback(() => {
    const next = nextChapterIndex(chapters, index);
    if (next != null) setIndex(next);
  }, [chapters, index]);

  /**
   * Warn before a refresh or tab close throws the coach conversation away.
   *
   * Only while there is something to lose — an unconditional handler nags
   * every student who ever opened the course. In-app navigation is covered by
   * the notice inside the panel instead: React Navigation does not route
   * through `beforeunload`, and the drawer unmounts this screen before any
   * confirm could be shown.
   */
  useEffect(() => {
    if (!coachTurns.length || typeof window === 'undefined') return;
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Chrome ignores custom text but requires returnValue to be set.
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [coachTurns.length]);

  /**
   * Go back to the video chapter that was playing when this position came up.
   *
   * A puzzle is cut out of the middle of a recording, so the explanation for it
   * is in the video chapter whose window contains the timestamp — usually the
   * one immediately before.
   */
  const replayMoment = useCallback(
    (seconds: number) => {
      const target = chapters.findIndex(c => {
        const segment = getVideoSegment(c?.pages?.[0]);
        return !!segment && seconds >= segment.start && seconds <= segment.end;
      });
      if (target < 0) return;
      // Remember where they came from. Leaving the puzzle resets its board,
      // move list and hints, so without a way back a student who taps the
      // timestamp mid-line loses the lot and lands on a video chapter gated on
      // watching to its end.
      setReturnToIndex(current => (current == null ? index : current));
      setIndex(target);
    },
    [chapters, index],
  );

  /**
   * Jump straight to a chapter from the chapter list. Clearing the pending
   * auto-advance here as well as in the effect below matters: a student who
   * solves a puzzle and then immediately picks a distant chapter would
   * otherwise be dragged one chapter forward from where they landed.
   */
  const goToChapter = useCallback((next: number) => {
    if (advanceTimeoutRef.current != null) {
      clearTimeout(advanceTimeoutRef.current);
      advanceTimeoutRef.current = undefined;
    }
    setIndex(next);
  }, []);

  // DEVIATION from the plan: the plan's onSolved handler scheduled
  // `setTimeout(advance, 600)` directly with no way to cancel it. Solving a
  // puzzle marks the page complete, which immediately enables the Continue
  // button (canAdvance flips true the same render) — so a student who clicks
  // Continue during that 600ms window races the scheduled auto-advance. Both
  // calls resolve `nextChapterIndex` against the *chapter index that was
  // current when the timeout was scheduled*, so the stale auto-advance can
  // fire a second `setIndex` after the student already moved on, silently
  // dragging them back to the chapter they just left. Tracking the timeout in
  // a ref and clearing it whenever `index` changes for any reason (manual
  // Back/Continue press, or the auto-advance itself) closes that race with no
  // change to the intended "solve auto-advances after a beat" behavior.
  useEffect(() => {
    return () => {
      if (advanceTimeoutRef.current != null) {
        clearTimeout(advanceTimeoutRef.current);
        advanceTimeoutRef.current = undefined;
      }
    };
  }, [index]);

  const shell = (children: React.ReactNode) => (
    <View
      style={{
        flex: 1,
        backgroundColor: F.teal,
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        gap: 12,
      }}>
      {children}
    </View>
  );

  if (isLoading) return shell(<ActivityIndicator color={F.white} />);
  if (error) {
    return shell(
      <CCText style={{color: F.white, fontSize: 16, fontWeight: '600'}}>
        Could not load this course.
      </CCText>,
    );
  }
  if (!chapter || !page) {
    return shell(
      <CCText style={{color: F.white, fontSize: 16, fontWeight: '600'}}>
        This course has no chapters yet.
      </CCText>,
    );
  }

  const isDone = completed.has(page.id);
  const canAdvance = isDone && nextChapterIndex(chapters, index) != null;

  const atEnd = nextChapterIndex(chapters, index) == null;
  const gateHint = isVideoPage(page)
    ? 'Complete the lesson to continue'
    : 'Solve the puzzle to continue';

  return (
    // 'courses' is in Application.tsx's plain-View list, so this screen is
    // handed a full-height container and flex:1 fills the window. Inside the
    // default ScrollView wrapper it would not: a ScrollView's content
    // container is `flex: 0 0 auto` and collapses to the content's height.
    <View style={{flex: 1, backgroundColor: F.teal}}>
      {/* Top bar, matching the LMS chapter player: back chevron + "Learn". */}
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          paddingHorizontal: 16,
          paddingVertical: 10,
        }}>
        {/* Leaves the course. This used to be wired to `setIndex(index - 1)` —
            the same thing as the Previous button in the action row — which left
            the player with no exit at all except a drawer tab, and that unmounts
            the screen and discards all progress. */}
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Back to courses"
          onPress={onExit}
          style={{flexDirection: 'row', alignItems: 'center', gap: 8}}>
          <Ionicons name="arrow-back" size={20} color={F.white} />
          <CCText style={{fontSize: 20, fontWeight: '600', color: F.white}}>Courses</CCText>
        </Pressable>
        <View style={{flexDirection: 'row', alignItems: 'center', gap: 16}}>
          <CCText style={{fontSize: 13, fontWeight: '600', color: F.white}}>
            {`Chapter ${chapter.chapter_number} of ${course?.chapter_count ?? chapters.length}`}
          </CCText>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="All chapters"
            onPress={() => setShowChapters(true)}>
            <CCText
              style={{
                fontSize: 13,
                fontWeight: '600',
                color: F.white,
                textDecorationLine: 'underline',
              }}>
              All chapters
            </CCText>
          </Pressable>
          {/* The coach opens from here rather than from the card at the foot of
              the page: on a puzzle chapter the board pushes that card below the
              fold, so the one thing a stuck student wants was the one thing
              they had to go looking for. */}
          <Pressable
            accessibilityRole="button"
            accessibilityState={{expanded: askCoach}}
            accessibilityLabel="Ask your coach about this course"
            onPress={() => setAskCoach(open => !open)}
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 6,
              borderWidth: 1,
              borderColor: 'rgba(255,255,255,0.45)',
              backgroundColor: askCoach ? 'rgba(255,255,255,0.16)' : 'transparent',
              borderRadius: 999,
              paddingHorizontal: 12,
              paddingVertical: 6,
            }}>
            <Ionicons name="school-outline" size={15} color={F.white} />
            <CCText style={{fontSize: 13, fontWeight: '600', color: F.white}}>
              {askCoach ? 'Close coach' : 'Ask your coach'}
            </CCText>
            {/* Closing is as discoverable as opening, and a conversation left
                behind a closed panel says so rather than vanishing. */}
            {!askCoach && coachTurns.length ? (
              <View
                style={{
                  width: 7,
                  height: 7,
                  borderRadius: 999,
                  backgroundColor: F.yellow,
                }}
              />
            ) : null}
          </Pressable>
        </View>
      </View>

      {/* `minHeight: 0` and `overflow: hidden` bound this row to what is left
          of the window below the header. Without them the row grows to fit
          whichever column is taller, the page itself starts scrolling, and the
          rail scrolls away with it — taking the composer and mic off screen
          exactly when they are wanted. */}
      <View
        style={{
          flex: 1,
          flexDirection: 'row',
          minHeight: 0,
          overflow: 'hidden',
        }}>
        {/* A plain View on a puzzle chapter, a ScrollView on a video one.
            A board inside a ScrollView loses its drag: the scroller claims the
            pointer as soon as it moves, so a piece picked up and dragged gets
            interpreted as a scroll and dropped back. This is the same reason
            the route is in Application.tsx's no-ScrollView list — that removes
            the wrapper the navigator adds, and this removes the one the screen
            was adding back. A puzzle chapter fits without scrolling; a video
            one has the lesson text under it and still needs to scroll. */}
        <Content
          style={{flex: 1, minHeight: 0}}
          {...(isPuzzlePage(page) ? {} : {contentContainerStyle: {flexGrow: 1}})}>
        <Box style={{paddingHorizontal: 24, paddingVertical: 24, gap: 16}}>
          {/* A puzzle chapter's own card already carries the title. Repeating
              it here just prints the same sentence twice on one screen. */}
          {isVideoPage(page) ? (
            <CCText style={{fontSize: 22, fontWeight: '600', color: F.white}}>
              {chapter.title}
            </CCText>
          ) : null}

          {isVideoPage(page) ? (
            <CourseVideoPlayer
              key={page.id}
              src={page.content}
              segment={segment}
              posterTitle={page.heading ?? undefined}
              onSegmentEnd={() => markComplete(true)}
            />
          ) : null}

          {isPuzzlePage(page) ? (
            <CoursePuzzleBoard
              key={page.id}
              page={page}
              bookUuid={bookUuid}
              coachStep={coachStep}
              onHint={showHint}
              onReplayMoment={replayMoment}
              // No auto-advance. Solving used to move the student on after
              // 600ms, which threw away the instructional moment: they never
              // saw the resulting position, could not re-read the lesson text
              // in light of the answer, and the "Nice! It's correct" card
              // flashed past. Solving now enables Continue and leaves the pace
              // to them, which is what the LMS player does.
              onSolved={() => markComplete(false)}
            />
          ) : null}

          {/* Action row, below the content as in the LMS player. */}
          <View
            style={{
              flexDirection: 'row',
              gap: 12,
              alignItems: 'center',
              flexWrap: 'wrap',
              paddingTop: 8,
            }}>
            <CourseIconButton
              glyph="arrow-back"
              label="Previous chapter"
              disabled={index === 0}
              onPress={() => setIndex(index - 1)}
            />
            <CourseButton
              label={atEnd && isDone ? 'Course complete' : 'Continue'}
              showArrow={!atEnd}
              disabled={!canAdvance}
              emphasised={canAdvance}
              onPress={advance}
            />
            {returnToIndex != null && returnToIndex !== index ? (
              <CourseButton
                label="Back to the puzzle"
                variant="review"
                onPress={() => {
                  const target = returnToIndex;
                  setReturnToIndex(null);
                  setIndex(target);
                }}
              />
            ) : null}
            {!isDone ? (
              <CCText style={{fontSize: 13, fontWeight: '600', color: F.white}}>
                {gateHint}
              </CCText>
            ) : null}
          </View>

        </Box>
        </Content>

        {/* The coach docks beside the lesson rather than under it. It is a real
            sibling column, not an overlay: the board is sized from the window
            rather than from its container, so it keeps its size and the rail
            cannot land on top of it — which matters because the whole point is
            reading the board *while* the coach answers. */}
        {askCoach && coachMode === 'rail' ? (
          <View
            style={{
              width: railWidth,
              // Both of these keep the transcript scrolling inside the rail
              // rather than stretching it: a flex child is floored at its
              // content height unless minHeight is cleared. `height: '100%'`
              // pins the rail to the row so the composer stays at the bottom
              // of the window rather than at the bottom of the conversation.
              height: '100%',
              minHeight: 0,
              overflow: 'hidden',
              borderLeftWidth: 1,
              borderLeftColor: F.border10,
              backgroundColor: F.white,
              borderTopLeftRadius: F.radius.card,
              borderBottomLeftRadius: F.radius.card,
              ...CARD_SHADOW,
              shadowOffset: {width: -10, height: 0},
            }}>
            <CourseCoachPanel
              open
              mode="rail"
              bookUuid={bookUuid}
              chapters={chapters}
              chapterCount={course?.chapter_count ?? chapters.length}
              currentChapter={chapter?.chapter_number ?? null}
              onGoToChapter={goToChapter}
              onClose={() => setAskCoach(false)}
              turns={coachTurns}
              onTurns={setCoachTurns}
              playingTurn={playingTurn}
              onPlayScript={playScriptFor}
              onStopScript={stopScript}
              onTranscribe={transcribe}
              speakingLine={coachStep ? coachStep.say : null}
            />
          </View>
        ) : null}
      </View>

      {/* Below the rail's width the lesson card would be squeezed to a ribbon,
          so the coach crosses the bottom instead — still never over the board,
          which sits above it. */}
      {askCoach && coachMode === 'sheet' ? (
        <View
          style={{
            borderTopWidth: 1,
            borderTopColor: F.border10,
            backgroundColor: F.white,
            borderTopLeftRadius: F.radius.card,
            borderTopRightRadius: F.radius.card,
            maxHeight: '55%',
            ...CARD_SHADOW,
            shadowOffset: {width: 0, height: -10},
          }}>
          <CourseCoachPanel
            open
            mode="sheet"
            bookUuid={bookUuid}
            chapters={chapters}
            chapterCount={course?.chapter_count ?? chapters.length}
              currentChapter={chapter?.chapter_number ?? null}
            onGoToChapter={goToChapter}
            onClose={() => setAskCoach(false)}
            turns={coachTurns}
            onTurns={setCoachTurns}
            playingTurn={playingTurn}
            onPlayScript={playScriptFor}
            onStopScript={stopScript}
              onTranscribe={transcribe}
              speakingLine={coachStep ? coachStep.say : null}
          />
        </View>
      ) : null}

      <CourseChapterList
        chapters={chapters}
        currentIndex={index}
        completedPageIds={completed}
        visible={showChapters}
        onSelect={goToChapter}
        onClose={() => setShowChapters(false)}
      />
    </View>
  );
}
